// [PURPOSE]: Register subclass-feature effects (SRD + custom Content Workshop) into
//   effectRegistry on subclass/level/subclassData change.
// [WHY]: srdCharacterData stores subclass features as plain string arrays;
//   srdSubclassFeatures.js provides the SRD mechanical wiring, and CW-authored
//   subclasses store their own `featureEffects` map. This hook is the single
//   bridge that walks both producers and registers them under prefix
//   `subclass:<ClassName>:<SubclassName>:<FeatureName>` (and `:<OptionName>` for
//   choice features). Mirrors useClassFeatureEffects.js / useRaceFeatureEffects.js.
// [INTEGRATION]: Mount once near the top of App.jsx alongside useClassFeatureEffects /
//   useRaceFeatureEffects. Watches playerStats.classes (which contains
//   className, level, subclass, subclassData per entry).
// [Phase 4b bucket 3 — APR 2026]: Subclass-feature wiring layer.
// [Phase 4b BC-12 — APR 2026]: Bridge for Content-Workshop-authored subclasses
//   so player-built effects round-trip identically to SRD ones (Goal #1: full
//   data-driven replicability).

import { useEffect } from 'react';
import { effectRegistry } from '../services/effectRegistry';
import { effectCalculator } from '../services/effectCalculator';
import { getActiveSubclassFeatures } from '../data/srdSubclassFeatures';
import { sourcesManager } from '../data/sourcesManager';

/**
 * Walks a dotted path inside subclassData (e.g., 'totemSpirit', 'hunterOptions.3').
 * Returns null when subclassData or choiceKey is missing; undefined when the path
 * doesn't resolve to a value.
 */
export function resolveChoice(subclassData, choiceKey) {
  if (!subclassData || !choiceKey) return null;
  return choiceKey.split('.').reduce((acc, k) => (acc != null ? acc[k] : undefined), subclassData);
}

/**
 * Extracts active feature entries from a Content-Workshop-authored custom subclass.
 *
 * Custom subclasses store their mechanical wiring in a top-level `featureEffects`
 * object that mirrors the per-feature shape used by SRD_SUBCLASS_FEATURES:
 *
 *   {
 *     featureEffects: {
 *       [featureName]: {
 *         minLevel: number,            // required — entries without it are skipped
 *         effects?: Effect[],          // always-on effects
 *         choiceKey?: string,          // dotted path inside subclassData
 *         choices?: { [option]: { effects, notes? } },
 *         notes?: string,
 *       }
 *     }
 *   }
 *
 * Returns [name, featureData] tuples filtered by minLevel <= classLevel, in
 * insertion order — same shape as getActiveSubclassFeatures().
 */
export function getActiveCustomSubclassFeatures(customSubclass, classLevel) {
  if (!customSubclass || typeof classLevel !== 'number') return [];
  const featureEffects = customSubclass.featureEffects;
  if (!featureEffects || typeof featureEffects !== 'object') return [];
  return Object.entries(featureEffects).filter(([, feature]) => {
    if (!feature || typeof feature.minLevel !== 'number') return false;
    return feature.minLevel <= classLevel;
  });
}

/**
 * Looks up the matching custom subclass entry for a given className+subclassName
 * by scanning sourcesManager.getAllSubclasses(). Returns null if none found or
 * if the lookup fails.
 *
 * Matches BOTH the subclass name AND its parent-class field so a wizard custom
 * subclass cannot bleed into a Fighter character with the same display name.
 */
function findCustomSubclass(className, subclassName) {
  let all;
  try {
    all = sourcesManager.getAllSubclasses();
  } catch (err) {
    console.warn('[useSubclassFeatureEffects] getAllSubclasses failed:', err);
    return null;
  }
  if (!all || typeof all !== 'object') return null;
  for (const entry of Object.values(all)) {
    if (!entry) continue;
    if (entry.name === subclassName && entry.class === className) {
      return entry;
    }
  }
  return null;
}

/**
 * Registers a single feature's effects (always-on + choice-dependent) under the
 * canonical prefix. Shared between SRD and custom paths so attribution and
 * cleanup are identical.
 */
function registerFeature({ className, subclassName, featureName, featureData, subclassData, playerStats }) {
  // Always-on effects
  if (Array.isArray(featureData.effects) && featureData.effects.length) {
    try {
      effectRegistry.register(
        `subclass:${className}:${subclassName}:${featureName}`,
        featureData.effects,
        playerStats
      );
    } catch (err) {
      console.warn(
        `[useSubclassFeatureEffects] failed: ${className}:${subclassName}:${featureName}`,
        err
      );
    }
  }

  // Choice-dependent effects
  if (featureData.choices && featureData.choiceKey) {
    const selected = resolveChoice(subclassData, featureData.choiceKey);
    const optionData = selected ? featureData.choices[selected] : null;
    if (optionData && Array.isArray(optionData.effects) && optionData.effects.length) {
      try {
        effectRegistry.register(
          `subclass:${className}:${subclassName}:${featureName}:${selected}`,
          optionData.effects,
          playerStats
        );
      } catch (err) {
        console.warn(
          `[useSubclassFeatureEffects] choice failed: ${className}:${subclassName}:${featureName}:${selected}`,
          err
        );
      }
    }
  }
}

/**
 * Top-level orchestrator — exported so the bridge can be exercised in unit tests
 * without a React renderer. Walks playerStats.classes, registers SRD subclass
 * features, then merges in Content-Workshop-authored custom subclasses for the
 * same class/subclass pair.
 *
 * Pure side-effect: writes to effectRegistry, then clears effectCalculator cache.
 */
export function registerSubclassFeaturesForCharacter(playerStats) {
  effectRegistry.removeByPrefix('subclass:');

  const classes = playerStats?.classes || [];
  for (const classEntry of classes) {
    const className = classEntry?.className;
    const subclassName = classEntry?.subclass;
    const classLevel = classEntry?.level || 0;
    const subclassData = classEntry?.subclassData || null;
    if (!className || !subclassName) continue;

    // 1. SRD subclass features
    const srdFeatures = getActiveSubclassFeatures(className, subclassName, classLevel);
    for (const [featureName, featureData] of srdFeatures) {
      registerFeature({
        className, subclassName, featureName, featureData, subclassData, playerStats,
      });
    }

    // 2. Custom subclass features (CW-authored, stored in sourcesManager).
    //    Only registers when the player has actually picked this custom subclass
    //    on this class entry (matched by both name AND parent class).
    const customSubclass = findCustomSubclass(className, subclassName);
    if (customSubclass) {
      const customFeatures = getActiveCustomSubclassFeatures(customSubclass, classLevel);
      for (const [featureName, featureData] of customFeatures) {
        registerFeature({
          className, subclassName, featureName, featureData, subclassData, playerStats,
        });
      }
    }
  }

  effectCalculator.clearCache();
}

export function useSubclassFeatureEffects(playerStats) {
  useEffect(() => {
    registerSubclassFeaturesForCharacter(playerStats);

    return () => {
      effectRegistry.removeByPrefix('subclass:');
      effectCalculator.clearCache();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerStats?.classes]);
}

export default useSubclassFeatureEffects;
